import { FC } from 'react';

import { Box } from '@mui/material';
import { FormikProps } from 'formik';

import { FieldInput } from 'src/components';
import { DropDown, NPIPhoneInput } from 'src/components/FormikComponents';
import { PersonalInfo } from 'src/components/PrincipalForm/PrincipalForm';
import { SearchInput } from 'src/components/SearchInput';
import { TITLE_OPTIONS } from 'src/constants';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useValidateEmail } from 'src/hooks';

interface PrincipalDetailsProps {
    name: string;
    principal: PersonalInfo;
    formik: FormikProps<any>;
    isEditingDisabled: boolean;
}

const PrincipalDetails: FC<PrincipalDetailsProps> = ({
    name,
    principal,
    formik,
    isEditingDisabled,
}) => {
    const { onValidateEmail } = useValidateEmail();

    const { merchantApplication } = useAddMerchantContext();

    const countryCode = merchantApplication?.partner?.countryDetails?.alpha2Code;

    return (
        <>
            <Box display="flex" gap="24px">
                <FieldInput
                    name={`${name}.firstName`}
                    label="First Name"
                    disabled={isEditingDisabled}
                />
                <FieldInput
                    name={`${name}.middleNames`}
                    label="Middle Name(s)"
                    disabled={isEditingDisabled}
                />
                <FieldInput
                    name={`${name}.lastName`}
                    label="Last Name"
                    disabled={isEditingDisabled}
                />
            </Box>
            <Box display="flex" gap="24px">
                <DropDown
                    name={`${name}.title`}
                    options={TITLE_OPTIONS}
                    label="Title"
                    placeholder="Select your title"
                    disabled={isEditingDisabled}
                />
                <FieldInput
                    name={`${name}.dateOfBirth`}
                    label="Date of Birth"
                    disabled={isEditingDisabled}
                />
            </Box>
            <Box display="flex" gap="24px">
                <FieldInput
                    name={`${name}.nationality`}
                    label="Nationality"
                    disabled={isEditingDisabled}
                />
                <SearchInput
                    name={`${name}.email`}
                    label="Email Address"
                    boxStyling={{ maxWidth: '50%' }}
                    value={principal.email}
                    onChange={(event) => {
                        return formik.handleChange(event);
                    }}
                    searchText="Validate"
                    onSearch={async (email) => {
                        return await onValidateEmail(
                            email,
                            () => {
                                return formik.setFieldError(
                                    `${name}.email`,
                                    'Provide a valid email address'
                                );
                            },
                            () => {
                                return formik.setFieldError(`${name}.email`, null);
                            }
                        );
                    }}
                    disabled={isEditingDisabled}
                />
            </Box>
            <Box display="flex" gap="24px">
                <NPIPhoneInput
                    fieldName={`${name}.mobileNumber`}
                    label="Mobile Number"
                    placeholder="Enter Mobile Number"
                    defaultCountry={countryCode}
                    disabled={isEditingDisabled}
                    dataTestId={`mobile-number-${name}`}
                />

                <NPIPhoneInput
                    fieldName={`${name}.telephoneNumber`}
                    label="Telephone Number"
                    placeholder="Enter Telephone Number"
                    defaultCountry={countryCode}
                    disabled={isEditingDisabled}
                    dataTestId={`telephone-number-${name}`}
                />
            </Box>
        </>
    );
};

export default PrincipalDetails;
